import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, ButtonGroup } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import InsertChartIcon from '@mui/icons-material/InsertChart'
import ModifyStyle from './PlanStyle'
import PlanCopyFromModal from './PlanCopyFromModal'
import TrendTargetBox from './TrendTargetBox'
import PlanChart from './PlanChart'
import NewPlanChart from './NewPlanChart'
import PlanService from '../../services/PlanService'
import moment from 'moment'

/**
 * Left panel controls (Copy/Target/Chart + chart area).
 *
 * The parent page is responsible for providing target `tabs` and `chartData`.
 * This keeps the panel generic and lets Monthly + Weekly decide how to source data.
 *
 * For the new weekly planning UI, pass `periodData` (array) instead of `chartData` (Map).
 * When `periodData` is provided, NewPlanChart is used (no stale closure issues).
 * When only `chartData` is provided, the legacy PlanChart is used (monthly/legacy path).
 */
function PlanLeftPanelControls({
  epochMonth,
  clientList = [],
  tabs = [],
  enableChart = true,
  chartData: chartDataProp,
  periodData,
  onOverridePlanData,
  chartLabels,
  chartXKeyLabel,
}) {
  const isCopy = JSON.parse(localStorage.getItem('showLoadCopyContainer'))
  const isTarget = JSON.parse(localStorage.getItem('showTargetContainer'))
  const isGraph = JSON.parse(localStorage.getItem('showGraphContainer'))

  const [showLoadCopyContainer, setShowLoadCopyContainer] = useState(isCopy === null ? true : isCopy)
  const [showTargetContainer, setShowTargetContainer] = useState(isTarget === null ? true : isTarget)
  const [showGraphContainer, setShowGraphContainer] = useState(isGraph === null ? true : isGraph)

  const [displayCopyFromModal, setDisplayCopyFromModal] = useState(false)

  // If the parent wants to receive overridePlanData (so it can update its grid), we keep
  // local state only as a fallback.
  const [localOverridePlanData, setLocalOverridePlanData] = useState(null)

  const handleOverridePlanData = (data) => {
    if (typeof onOverridePlanData === 'function') {
      onOverridePlanData(data)
    } else {
      setLocalOverridePlanData(data)
    }
  }

  const [displayMonths, setDisplayMonths] = useState([])

  const chartData = useMemo(() => {
    if (chartDataProp && typeof chartDataProp?.get === 'function') return chartDataProp
    return new Map()
  }, [chartDataProp, localOverridePlanData])

  const labels = useMemo(() => {
    if (Array.isArray(chartLabels) && chartLabels.length) return chartLabels
    return displayMonths
  }, [chartLabels, displayMonths])

  const intervalRef = useRef()

  useEffect(() => {
    localStorage.setItem('showLoadCopyContainer', JSON.stringify(showLoadCopyContainer))
    localStorage.setItem('showTargetContainer', JSON.stringify(showTargetContainer))
    localStorage.setItem('showGraphContainer', JSON.stringify(showGraphContainer))
  }, [showLoadCopyContainer, showTargetContainer, showGraphContainer])

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (!displayMonths.length && epochMonth) {
      const startMonth = PlanService.convertEpochMonthToDate(epochMonth)
      const months = []
      for (let i = 0; i < 12; i++) {
        months.push(moment(startMonth).add(i, 'months').format('MMM'))
      }
      setDisplayMonths(months)
    }
  }, [epochMonth, displayMonths.length])

  const toggleCopyPanel = () => setShowLoadCopyContainer((s) => !s)
  const toggleTargetPanel = () => setShowTargetContainer((s) => !s)
  const toggleGraphPanel = () => setShowGraphContainer((s) => !s)

  return (
    <>
      <Box sx={ModifyStyle.loadPlan}>
        <ButtonGroup
          id={'left-button-group'}
          variant="outlined"
          size="small"
          sx={{ mb: 2 }}
          aria-label="outlined button group"
        >
          <Button variant={showLoadCopyContainer ? 'contained' : 'outlined'} onClick={toggleCopyPanel}>
            <ContentCopyIcon sx={{ fontSize: '12px' }} /> Copy
          </Button>
          <Button variant={showTargetContainer ? 'contained' : 'outlined'} onClick={toggleTargetPanel}>
            <TrackChangesIcon sx={{ fontSize: '12px' }} /> Target
          </Button>
          <Button variant={showGraphContainer ? 'contained' : 'outlined'} onClick={toggleGraphPanel}>
            <InsertChartIcon sx={{ fontSize: 'small' }} /> Chart
          </Button>
        </ButtonGroup>

        {showLoadCopyContainer ? (
          <Box id={'load-copy-container'}>
            <Button onClick={() => setDisplayCopyFromModal(true)} fullWidth variant="outlined">
              Load Plan
            </Button>
            <PlanCopyFromModal
              open={displayCopyFromModal}
              onClose={() => setDisplayCopyFromModal(false)}
              clientList={clientList}
              setOverridePlanData={handleOverridePlanData}
            />
          </Box>
        ) : null}
      </Box>

      {/* Targets panel */}
      {showTargetContainer ? <TrendTargetBox data={tabs} /> : null}

      {/* Chart panel */}
      {enableChart && showGraphContainer ? (
        <Box id={'graph-container'} sx={ModifyStyle.graphContainer}>
          {Array.isArray(periodData) && periodData.length > 0
            ? <NewPlanChart periodData={periodData} xKeyLabel={chartXKeyLabel || 'week'} />
            : <PlanChart sx={{ mt: 2 }} labels={labels} xKeyLabel={chartXKeyLabel || 'month'} displayMonths={displayMonths} chartData={chartData && typeof chartData.get === 'function' ? chartData : new Map()} />
          }
        </Box>
      ) : null}
    </>
  )
}

export default PlanLeftPanelControls

